import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Input } from '../atoms/Input';
import { useAuth } from '../hooks/useAuth';
import { useNavigate, useLocation } from 'react-router-dom';
import { PATHS } from '../commons/paths';
import { useTranslationWithLang } from 'hooks/useTranslationWithLang';
import { PlayArrow } from '@mui/icons-material';
import { TransparentFooter } from 'templates/TransparentFooter';
import { BackButton } from 'atoms/BackButton';
import { Button } from 'atoms/Button';
import { StyledFormError } from 'organisms/u001/StyledFormError';
import { useConfirmMfaSchema } from 'hooks/u001/useConfirmMfaSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { ConfirmMfaSchemaType } from 'commons/login';
import { SubmitHandler, useForm } from 'react-hook-form';
import { AppContext } from 'templates/AppContextProvider';
import { useGetUser } from 'hooks/u006/useGetUserHooks';
import { useGetLineLinkUrl } from 'hooks/u013/useGetLineLinkUrl';
import LoadingModal from 'organisms/LoadingModal';
import { NotificationReceiver } from 'domains/User';
import { useUpdateLastLogin } from 'hooks/u001/useUpdateLastLoginHooks';

export function ConfirmMfaCode() {
  // context
  const ctx = useContext(AppContext);
  // hooks
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();
  const [data, , , refresh] = useGetUser(ctx.userId);
  const { t: tCommon } = useTranslationWithLang('commons');
  const ConfirmMfaSchema = useConfirmMfaSchema();
  const { getLineLinkUrl } = useGetLineLinkUrl();
  const { currentLang } = useContext(AppContext);
  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
  } = useForm<ConfirmMfaSchemaType>({
    resolver: zodResolver(ConfirmMfaSchema),
  });
  // state
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [completeLogin, setCompleteLogin] = useState<boolean>(false);
  const [updateLastLogin, updateLastLoginLoading] = useUpdateLastLogin();
  const [verifyFailedCount, setVerifyFailedCount] = useState<number>(0);
  // effect
  useEffect(() => {
    clearErrors();
  }, [currentLang, clearErrors]);
  useEffect(() => {
    // ログイン完了後、Line連携処理開始
    if (completeLogin) {
      setIsLoading(true);
      refresh();
      // effect内非同期関数定義
      const linkLineAccount = async (id: string, rev: number, token: string) => {
        const res = await getLineLinkUrl(id, rev, token);
        if (res) {
          window.location.href = res.link;
        } else {
          setErrorMessage(tCommon('TU000_091')); // LINE連携に失敗しました
        }
        setIsLoading(false);
      };
      const userId = ctx.userId;
      const revision = data?.user_info_revision === undefined ? -1 : data.user_info_revision;
      if (userId && linkToken && revision > -1) linkLineAccount(userId, revision, linkToken);
    }
  }, [completeLogin, ctx.userId, data?.user_info_revision]);
  useEffect(() => {
    if (verifyFailedCount >= 5) navigate(PATHS.LOGIN);
  }, [verifyFailedCount]);
  // function
  const onClickConfirmMfaCode: SubmitHandler<ConfirmMfaSchemaType> = useCallback(
    async (form) => {
      setErrorMessage('');
      setIsLoading(true);
      if (auth.signInUser == null) {
        setVerifyFailedCount((prev) => prev + 1);
        setErrorMessage(tCommon('TU000_046'));
        setIsLoading(false);
        setCompleteLogin(false);
        return;
      }
      const result = await auth.confirmMfaCode(auth.signInUser, form.mfaCode);
      if (result.success) {
        await updateLastLogin({
          onSuccess: () => {
            setCompleteLogin(true);
            // ログイン画面でリダイレクトを指定していた場合リダイレクトする
            if (auth.signInUser?.opts?.redirect) {
              navigate(auth.signInUser.opts.redirect);
              return;
            }
            // LINE連携以外の場合はマイページに遷移.LINE連携時は画面遷移しない。
            if (!linkToken) navigate(PATHS.MYPAGE);
          },
          onError: (err) => {
            console.error(err);
          },
        });
      } else {
        setVerifyFailedCount((prev) => prev + 1);
        setIsLoading(false);
        setCompleteLogin(false);
        setErrorMessage(
          result.message === 'NetworkError'
            ? tCommon('TU000_057')
            : result.message === 'NotAuthorizedException'
              ? tCommon('TU000_012')
              : tCommon('TU000_058')
        );
      }
    },
    [navigate, auth, tCommon]
  );

  const handleMailLogin = () => {
    if (auth.signInUser?.opts?.redirect) {
      const queryParam = new URLSearchParams();
      queryParam.set('redirect', auth.signInUser.opts.redirect);
      navigate(PATHS.LOGIN + '?' + queryParam.toString(), {
        state: { recipient: NotificationReceiver.MailAddress },
      });
      return;
    }
    navigate(PATHS.LOGIN, {
      state: { recipient: NotificationReceiver.MailAddress },
    });
  };
  // value
  const linkToken = (location.state?.linkToken as string) || null;
  const buttonLabel = useMemo(
    () => (linkToken ? tCommon('TU000_092') : tCommon('TU000_026')), // LINE連携 : ログイン
    [linkToken]
  );
  return (
    <>
      {(isLoading || updateLastLoginLoading) && <LoadingModal />}
      <div
        className="flex flex-col items-center justify-center pb-20 w-full h-full max-h-full overflow-x-hidden"
        style={{
          height: '100vh',
          maxHeight: '100vh',
          background: 'var(--c-background)',
        }}
      >
        <main className="w-[400px] flex flex-col items-center m-20">
          <img
            src="/logo_spotu.png"
            alt="logo"
            title="logo"
            aria-label="logo"
            style={{ width: '350px', height: 'auto' }}
          />
          <form
            className="w-[300px] flex flex-col items-center mt-4"
            onSubmit={handleSubmit(onClickConfirmMfaCode)}
          >
            <p className="text-[--c-text] text-sm mt-10" aria-live="assertive">
              {tCommon('TU000_079')}
            </p>
            <p className="text-[--c-text] text-sm m-2 mb-4" aria-live="assertive">
              {tCommon('TU000_031')}
            </p>
            <Input
              className="mb-6 px-5 rounded-full"
              {...register('mfaCode')}
              placeholder={tCommon('TU000_032')}
            />
            <button
              type="button"
              onClick={() => navigate(PATHS.ABOUT_MFA_CODE_SENDING)}
              className="text-[--c-text] text-sm flex"
              title={tCommon('TU000_108')}
            >
              <PlayArrow sx={{ fontSize: 'clamp(1rem, 0.583rem + 0.83vw, 1.25rem)' }} />
              {tCommon('TU000_108')}
            </button>
            <button
              type="button"
              onClick={() => navigate(PATHS.LOGIN_ERROR)}
              className="text-[--c-text] text-sm flex"
              title={tCommon('TU000_033')}
            >
              <PlayArrow sx={{ fontSize: 'clamp(1rem, 0.583rem + 0.83vw, 1.25rem)' }} />
              {tCommon('TU000_033')}
            </button>
            {ctx.isGlobal && auth.signInUser?.user?.challengeParam?.isTaiwan === 'true' && (
              <button
                type="button"
                onClick={handleMailLogin}
                className="text-[--c-text] text-sm flex mt-2"
                title={tCommon('TU000_062')}
              >
                <PlayArrow sx={{ fontSize: 'clamp(1rem, 0.583rem + 0.83vw, 1.25rem)' }} />
                {tCommon('TU000_062')}
              </button>
            )}
            <StyledFormError errorMessage={errors.mfaCode?.message || errorMessage || ''} />
            <div className="flex items-center">
              <BackButton className="mr-2 w-[9rem]" />
              <Button type="submit" label={buttonLabel} className="w-[9rem]" />
            </div>
          </form>
        </main>
        <TransparentFooter />
      </div>
    </>
  );
}
